/**
 * SearchResultCard Component
 * Displays a single search result with text highlighting
 * Features:
 * - Highlight matching text in name and description
 * - Content type icon and color
 * - Folder path breadcrumb
 * - Tag display
 * - Quick actions (view, edit)
 */

import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  FileText,
  Hash,
  Image,
  Table,
  Mail,
  Presentation,
  Folder,
  Eye,
  Edit,
  ExternalLink,
  Calendar,
  User,
} from 'lucide-react'
import type { ContentItem, ContentItemType } from '@/lib/api/types'

// ============================================
// Types
// ============================================

export interface SearchResultCardProps {
  item: ContentItem
  searchQuery?: string
  onView?: (item: ContentItem) => void
  onEdit?: (item: ContentItem) => void
  className?: string
}

// ============================================
// Constants
// ============================================

const CONTENT_TYPE_ICONS: Record<ContentItemType, React.ElementType> = {
  Text: FileText,
  DataPoint: Hash,
  Image: Image,
  Table: Table,
  EmailSnippet: Mail,
  SlideElement: Presentation,
}

const CONTENT_TYPE_COLORS: Record<ContentItemType, string> = {
  Text: 'from-blue-400 to-blue-600',
  DataPoint: 'from-green-400 to-green-600',
  Image: 'from-purple-400 to-purple-600',
  Table: 'from-amber-400 to-amber-600',
  EmailSnippet: 'from-rose-400 to-rose-600',
  SlideElement: 'from-orange-400 to-orange-600',
}

const CONTENT_TYPE_LABELS: Record<ContentItemType, string> = {
  Text: 'Text',
  DataPoint: 'Data Point',
  Image: 'Image',
  Table: 'Table',
  EmailSnippet: 'Email',
  SlideElement: 'Slide',
}

// ============================================
// Helper Functions
// ============================================

/**
 * Highlights matching text in a string
 * Returns an array of React nodes with highlighted spans
 */
function highlightText(text: string, query: string): React.ReactNode {
  if (!query.trim()) return text

  const parts: React.ReactNode[] = []
  const lowerText = text.toLowerCase()
  const lowerQuery = query.toLowerCase()
  let lastIndex = 0

  // Find all matches
  let startIndex = lowerText.indexOf(lowerQuery)
  while (startIndex !== -1) {
    // Add text before match
    if (startIndex > lastIndex) {
      parts.push(text.slice(lastIndex, startIndex))
    }

    // Add highlighted match
    parts.push(
      <mark
        key={startIndex}
        className="bg-yellow-200 dark:bg-yellow-800/50 text-inherit rounded px-0.5"
      >
        {text.slice(startIndex, startIndex + query.length)}
      </mark>
    )

    lastIndex = startIndex + query.length
    startIndex = lowerText.indexOf(lowerQuery, lastIndex)
  }

  // Add remaining text
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex))
  }

  return parts.length > 0 ? <>{parts}</> : text
}

/**
 * Formats a date for display
 */
function formatDate(dateString: string): string {
  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

  if (diffDays === 0) {
    return 'Today'
  } else if (diffDays === 1) {
    return 'Yesterday'
  } else if (diffDays < 7) {
    return `${diffDays} days ago`
  } else {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
    })
  }
}

// ============================================
// SearchResultCard Component
// ============================================

export function SearchResultCard({
  item,
  searchQuery = '',
  onView,
  onEdit,
  className,
}: SearchResultCardProps) {
  const navigate = useNavigate()

  const Icon = CONTENT_TYPE_ICONS[item.type]
  const colorClass = CONTENT_TYPE_COLORS[item.type]
  const typeLabel = CONTENT_TYPE_LABELS[item.type]

  // Memoize highlighted text
  const highlightedName = useMemo(
    () => highlightText(item.name, searchQuery),
    [item.name, searchQuery]
  )

  const highlightedDescription = useMemo(
    () => (item.description ? highlightText(item.description, searchQuery) : null),
    [item.description, searchQuery]
  )

  // Handlers
  const handleView = () => {
    if (onView) {
      onView(item)
    } else {
      navigate(`/content-hub/item/${item.id}`)
    }
  }

  const handleEdit = () => {
    if (onEdit) {
      onEdit(item)
    } else {
      navigate(`/content-hub/item/${item.id}/edit`)
    }
  }

  return (
    <div
      className={cn(
        'group relative p-4 rounded-xl',
        'bg-white dark:bg-neutral-900',
        'border border-neutral-200 dark:border-neutral-700',
        'hover:border-blue-300 dark:hover:border-blue-700',
        'hover:shadow-md transition-all duration-200',
        className
      )}
    >
      <div className="flex items-start gap-4">
        {/* Type Icon */}
        <div
          className={cn(
            'flex-shrink-0 p-2.5 rounded-xl bg-gradient-to-br',
            colorClass
          )}
        >
          <Icon className="h-5 w-5 text-white" />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Type Badge */}
          <div className="flex items-center gap-2 mb-1">
            <Badge
              variant="secondary"
              className="text-xs font-normal px-1.5 py-0"
            >
              {typeLabel}
            </Badge>
            {item.folderName && (
              <div className="flex items-center gap-1 text-xs text-neutral-500 dark:text-neutral-400">
                <Folder className="h-3 w-3" />
                <span className="truncate max-w-[150px]">{item.folderName}</span>
              </div>
            )}
          </div>

          {/* Name */}
          <h3 className="text-base font-semibold text-neutral-900 dark:text-white mb-1 line-clamp-1">
            {highlightedName}
          </h3>

          {/* Description */}
          {highlightedDescription && (
            <p className="text-sm text-neutral-600 dark:text-neutral-400 line-clamp-2 mb-2">
              {highlightedDescription}
            </p>
          )}

          {/* Tags */}
          {item.tags && item.tags.length > 0 && (
            <div className="flex flex-wrap gap-1 mb-2">
              {item.tags.slice(0, 5).map((tag) => (
                <Badge
                  key={tag}
                  variant="outline"
                  className="text-xs px-1.5 py-0 text-neutral-500 dark:text-neutral-400"
                >
                  #{highlightText(tag, searchQuery)}
                </Badge>
              ))}
              {item.tags.length > 5 && (
                <span className="text-xs text-neutral-400">
                  +{item.tags.length - 5} more
                </span>
              )}
            </div>
          )}

          {/* Metadata */}
          <div className="flex items-center gap-4 text-xs text-neutral-500 dark:text-neutral-400">
            <div className="flex items-center gap-1">
              <Calendar className="h-3 w-3" />
              <span>{formatDate(item.updatedAt)}</span>
            </div>
            {item.createdBy && (
              <div className="flex items-center gap-1">
                <User className="h-3 w-3" />
                <span className="truncate max-w-[100px]">{item.createdBy}</span>
              </div>
            )}
            {typeof item.usageCount === 'number' && item.usageCount > 0 && (
              <div className="flex items-center gap-1">
                <ExternalLink className="h-3 w-3" />
                <span>Used {item.usageCount}x</span>
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex-shrink-0 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={handleView}
            className="h-8 w-8 text-neutral-500 hover:text-blue-600"
          >
            <Eye className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={handleEdit}
            className="h-8 w-8 text-neutral-500 hover:text-green-600"
          >
            <Edit className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  )
}

export default SearchResultCard
